/**
 * Retorna o nome da coluna marcada como primária
 */
export const getPrimaryField = (columns) => {
  return columns.find((col) => col.primary)?.name || null;
};

/**
 * Monta o payload da entidade para o JSON
 *
 * indexes (índices declarados): undefined some do JSON — chave ausente = o backend não mexe
 * em índice nenhum; [] declara que não deve sobrar índice gerenciado (remove os do banco).
 * Por isso quem chama só passa a lista quando a chave veio carregada da entidade ou quando
 * os índices foram mexidos na edição (ver NewEntityTable).
 */
export const buildEntityPayload = ({
  table,
  primary,
  description,
  beforeExecution,
  beforeExecutionType,
  beforeExecutionAsync,
  audit,
  command,
  security,
  isView,
  isProcedure,
  typeView,
  columns,
  indexes,
  entities,
  mainTable,
  whereClause,
  joins,
  procedureParams,
  procedureType,
  schema,
  scope,
  dropObject,
}) => {
  const payload = {
    table,
    primary,
    description,
    beforeExecution,
    beforeExecutionType,
    beforeExecutionAsync,
    audit,
    command,
    security,
    isView,
    isProcedure,
    typeView,
    columns,
    indexes,
    entities,
    mainTable,
    whereClause,
    joins,
    procedureParams,
    procedureType,
    schema,
    scope,
    dropObject,
  };
  
  // Remove campos undefined para evitar poluição do JSON
  Object.keys(payload).forEach(key => {
    if (payload[key] === undefined) {
      delete payload[key];
    }
  });
  
  return payload;
};

/**
 * Cria um arquivo JSON para download e upload
 */
export const createJsonFile = (data, filename) => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  return new File([blob], filename, { type: "application/json" });
};

/**
 * Dispara o download local do arquivo no navegador
 */
export const triggerDownload = (file) => {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Avisos devolvidos pelo upload da entidade — corpo { warnings: [...] }; corpo vazio = sem avisos.
 * Cada aviso vira texto (string direto, ou message/mensagem quando vier objeto).
 */
export const extractUploadWarnings = (data) => {
  const list = data && typeof data === "object" && Array.isArray(data.warnings) ? data.warnings : [];
  return list
    .map((warning) =>
      typeof warning === "string"
        ? warning
        : warning?.message || warning?.mensagem || JSON.stringify(warning)
    )
    .filter((warning) => warning && String(warning).trim());
};

/**
 * Faz upload do arquivo da entidade via API e mostra mensagem
 *
 * onWarnings (opcional): recebe a lista de avisos do backend (falha no banco que não impede o
 * salvamento, ex.: índice não aplicado). Sem ele os avisos vão no próprio snackbar.
 * O retorno continua true/false.
 */
export const uploadEntity = async (
  entityName,
  file,
  showMessage,
  apiMethod,
  comment = '',
  onWarnings
) => {
  try {
    const response = await apiMethod(entityName, file, comment);
    // entity created successfully
    const warnings = extractUploadWarnings(response?.data);
    if (warnings.length > 0) {
      showMessage(
        onWarnings
          ? `Entidade salva com ${warnings.length} aviso(s)`
          : `Entidade salva com aviso(s): ${warnings.join(" | ")}`,
        "warning",
        8000
      );
      if (onWarnings) onWarnings(warnings);
    } else {
      showMessage("Entidade salva com sucesso!", "success");
    }
    return true;
  } catch (error) {
    console.error("Erro ao salvar entidade:", error);
    const apiMsg =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message;
    showMessage(apiMsg ? `Erro: ${apiMsg}` : "Erro ao salvar entidade", "error");
    return false;
  }
};

/**
 * Estilos gerais
 */
export const styleNotFull = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: {
    xs: "95%",
    sm: "90%",
    md: "85%",
    lg: "80%",
    xl: "75%",
  },
  maxWidth: "1400px",
  maxHeight: "90vh",
  overflow: "hidden",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: { xs: 2, sm: 2.5, md: 3 },
  display: "flex",
  flexDirection: "column",
};

export const styleFull = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  bgcolor: "background.paper",
  p: { xs: 2, sm: 3, md: 4 },
  boxShadow: 24,
  width: "100vw",
  height: "100vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  zIndex: 1300,
};

export const styleJoinModal = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  maxHeight: "80vh",
  overflow: "auto",
};

export const joinTypes = [
  { label: "JOIN", value: "JOIN" },
  { label: "LEFT OUTER JOIN", value: "LEFT OUTER JOIN" },
  { label: "LEFT JOIN", value: "LEFT JOIN" },
  { label: "RIGHT JOIN", value: "RIGHT JOIN" },
  { label: "INNER JOIN", value: "INNER JOIN" },
  { label: "RIGHT OUTER JOIN", value: "RIGHT OUTER JOIN" },
];

export const mapColumnsFromEntityViewEdit = (entityToEdit) =>
  entityToEdit?.config?.columns?.map((col) => ({
    column: col,
    entityName: col.entityName,
  })) || [];

export const mapJoinsFromEntityViewEdit = (entityToEdit) =>
  entityToEdit?.config?.joins?.map((j) => ({
    initTable: j.initTable,
    initField: j.initField,
    destinationTable: j.destinationTable,
    destinationField: j.destinationField,
    joinType: j.join?.split(" ")[0] + " " + j.join?.split(" ")[1],
    text: j.join,
    id: Date.now(),
  })) || [];

export const mapSelectedEntitiesFromColumnsViewEdit = (entityToEdit, entities) => {
  const entityNames = [
    ...new Set(entityToEdit?.config?.columns?.map((c) => c.entityName)),
  ];
  return entities.filter((ent) => entityNames.includes(ent.entity));
};

/**
 * Converte dados de view builder para SQL command
 * Gera um SELECT a partir das entidades, colunas, joins e where clause
 */
export const convertBuilderToCommand = ({
  selectedEntities,
  selectedColumns,
  joinConditions,
  whereClause,
}) => {
  if (!selectedColumns || selectedColumns.length === 0) {
    return '';
  }

  // Monta a parte SELECT com os campos
  const selectFields = selectedColumns.map((sc) => {
    const { column, entityName } = sc;
    const aggregator = column.rules?.aggregator;
    const fieldName = `${entityName}.${column.name}`;
    
    if (aggregator && aggregator !== 'NONE') {
      return `${aggregator}(${fieldName}) AS ${column.name}`;
    }
    return `${fieldName} AS ${column.name}`;
  }).join(',\n    ');

  // Identifica a tabela principal (primeira entidade selecionada)
  const mainEntity = selectedEntities && selectedEntities.length > 0 
    ? selectedEntities[0] 
    : null;

  if (!mainEntity) {
    return `SELECT\n    ${selectFields}`;
  }

  // Cria um mapa de entity (alias) para table (nome físico da tabela)
  const entityToTableMap = {};
  if (selectedEntities) {
    selectedEntities.forEach(ent => {
      entityToTableMap[ent.entity] = ent.config?.table || ent.entity;
    });
  }

  let sql = `SELECT\n    ${selectFields}\nFROM ${mainEntity.config?.table} AS ${mainEntity.entity}`;

  // Adiciona os JOINs
  if (joinConditions && joinConditions.length > 0) {
    joinConditions.forEach((join) => {
      const { joinType, initTable, initField, destinationTable, destinationField } = join;
      // Busca o nome físico da tabela de destino
      const destinationPhysicalTable = entityToTableMap[destinationTable] || destinationTable;
      sql += `\n${joinType || 'LEFT JOIN'} ${destinationPhysicalTable} AS ${destinationTable} ON ${initTable}.${initField} = ${destinationTable}.${destinationField}`;
    });
  }

  // Adiciona WHERE clause
  if (whereClause && whereClause.trim()) {
    sql += `\nWHERE ${whereClause.trim()}`;
  }

  // Verifica se tem algum agrupador (agregação)
  const hasAggregation = selectedColumns.some(
    (sc) => sc.column.rules?.aggregator && sc.column.rules.aggregator !== 'NONE'
  );

  if (hasAggregation) {
    // Campos sem agregação vão para o GROUP BY
    const groupByFields = selectedColumns
      .filter((sc) => !sc.column.rules?.aggregator || sc.column.rules.aggregator === 'NONE')
      .map((sc) => `${sc.entityName}.${sc.column.name}`)
      .join(', ');
    
    if (groupByFields) {
      sql += `\nGROUP BY ${groupByFields}`;
    }
  }

  return sql;
};

/**
 * Converte dados de view command para builder
 * Retorna um objeto indicando que a conversão não é automática
 */
export const convertCommandToBuilder = () => {
  return {
    canConvert: false,
    message: 'A conversão de Command para Builder não é automática. Você precisará criar manualmente.'
  };
};
